<script setup lang="ts">
import { computed, useTemplateRef, useElementSize, whenever, definePageMeta, usePreviewBridge } from '#imports'
import PromotionModal, { type PromotionModalProps } from '@promotion/components/Promotion-Modal.vue'
import { PromotionClient } from '@core/types'
import { z } from 'zod/v4'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true
})

// backoffice-core's buildPreviewModalPayload mirrors this schema, so it stays as it is.
const DataSchema = z.looseObject({
    isPlaygame: z.boolean().default(false),
    promotion: z.looseObject({
        success: z.boolean().default(true),
        // Promotion
        Promotion: z.looseObject({
            expires_at: z.string().optional(),
            external_id: z.string(),
            foreign_model: z.string(),
            foreign_model_id: z.union([z.string(), z.number()]).nullable(),
            id: z.string(),
            max_reclaimable_interval_iterations: z.string().nullable(),
            reward_image_path: z.string(),
            trigger_type: z.union([z.string(), z.number()]),
            requirement_type: z.string(),
            start_at: z.string(),
            terms_hyperlink: z.string().optional()
        }),
        declineText: z.string().optional(),
        userMessage: z.string(),
        title: z.string()
    })
})

const { data, send } = usePreviewBridge(DataSchema)

const offer = computed((): PromotionModalProps['promotion'] | undefined => {
    if (!data.value?.success) return

    const { promotion } = data.value.data

    return {
        // The preview never answers the offer.
        acceptUrl: '',
        declineUrl: '',
        ctaText: typeof promotion.ctaText === 'string' ? promotion.ctaText : null,
        declineText: promotion.declineText ?? null,
        hasPaymentRequirement: false,
        rewardImagePath: promotion.Promotion.reward_image_path || null,
        // backoffice-core always previews the placeholder foreign_model 'Bonus'.
        rewardType: PromotionClient.BonusType.CASH,
        title: promotion.title,
        userMessage: promotion.userMessage,
    }
})

const modal = useTemplateRef('modal')
// The whole dialog box: the step's content is teleported into the sheet, which adds its own chrome around it.
const { height } = useElementSize(() => modal.value?.el?.closest<HTMLElement>('.dialog') ?? null, undefined, { box: 'border-box' })

whenever(height, (height) => {
    send({
        type: 'resize',
        payload: {
            height: `${height}px`,
            width: `414px`
        }
    })
})
</script>

<template>
    <UIApp>
        <div v-if="!data">
            <UILoader bg>
                Waiting for data
            </UILoader>
        </div>

        <template v-else>
            <div v-if="data.error">
                <pre>{{ data.error.issues }}</pre>
            </div>

            <PromotionModal v-else-if="offer" ref="modal" :promotion="offer" />
        </template>
    </UIApp>
</template>
